import React, { useContext, useEffect, useState } from "react";
import {
  View,
  Text,
  Switch,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  PixelRatio,
  Dimensions,
  Platform,
  Modal,
  ActivityIndicator,
  TextInput,
} from "react-native";
import { Image } from "expo-image";
import { MaterialCommunityIcons, Ionicons, FontAwesome } from "@expo/vector-icons";
import AppleSignin from "react-apple-signin-auth";
import Colours from "../../../utils/Colours";
import TopHeaderComponent from "../../aTopHeader/TopHeaderComponent";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useWebSafeArea } from "../../../hooks/useWebHeights";
import ErrorAlertComp from "../../../alertComp/ErrorAlertComp";
import { SafeAreaView } from "react-native-safe-area-context";
import axios from "../../../api/axios";
import { useGoogleAuth } from "../../../hooks/useGoogleAuth";
import AppContext from "../../../context/AppContext";
import { useAppleAuth } from "../../../hooks/useAppleAuth";
import useAuth from "../../../hooks/useAuth";

const { width, height } = Dimensions.get("window");

// --- RESPONSIVE TOOLKIT ---
const BASE_WIDTH = 375;
const BASE_HEIGHT = 812;

const dynamicFontSize = (size) => {
  const scaleFactor = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
  return Math.round(PixelRatio.roundToNearestPixel(size * scaleFactor));
};
const dynamicSpacing = (size) => (width / BASE_WIDTH) * size;
const dynamicSize = (size) => (width / BASE_WIDTH) * size;

// --- MOCK MENU DATA FOR PREVIEW ---
const sampleMenuItems = [
  {
    _id: "1",
    name: "Don Julio 1942 Tequila",
    description: "Smooth, aged premium tequila with rich caramel and chocolate notes.",
    image:
      "https://images.unsplash.com/photo-1527061011665-3652c757a4d4?q=80&w=500&auto=format&fit=crop",
    regularPrice: 250000,
    doingsPrice: 320000,
  },
  {
    _id: "2",
    name: "Moët & Chandon Brut Impérial",
    description: "Classic champagne bursting with bright fruitiness and elegant maturity.",
    image:
      "https://images.unsplash.com/photo-1575444758702-4a6b9222336e?q=80&w=500&auto=format&fit=crop",
    regularPrice: 120000,
    doingsPrice: 160000,
  },
  {
    _id: "3",
    name: "Suya Platter Deluxe",
    description: "Spicy grilled beef skewers served with fresh onions, tomatoes, and extra spice.",
    regularPrice: 25000,
    doingsPrice: 35000,
  },
];

const ChooseSpendModePrepaid = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const webSafeArea = useWebSafeArea();

  const { showId } = route.params || {};

  const [loading, setLoading] = useState(true);
  const [isLoadingOTP, setIsLoadingOTP] = useState(false);
  const [showData, setShowData] = useState(null);
  const [promoterData, setPromoterData] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [venueData, setVenueData] = useState(null);
  const [whatsappNumber, setWhatsAppNumber] = useState("");

  useEffect(() => {
    const fetchShowDetails = async () => {
      if (!showId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(`/get_a_show/get_a_show/${showId}`);

        if (response?.data?.success) {
          const rawShow = response.data.data;

          setShowData({
            id: rawShow._id,
            title: rawShow.caption || "Special Event",
            coverImage: rawShow.image,
            date: rawShow.startDate
              ? new Date(rawShow.startDate).toLocaleDateString("en-US", {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                })
              : "TBD",
            time: rawShow.startDate
              ? new Date(rawShow.startDate).toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "TBD",
            description: rawShow.caption,
            menuItems:
              rawShow.menuItems && rawShow.menuItems.length > 0
                ? rawShow.menuItems
                : sampleMenuItems,
          });

          if (rawShow.hostVenue) {
            setVenueData({
              id: rawShow.hostVenue._id,
              name: rawShow.hostVenue.brandName || rawShow.hostVenue.name,
              image: rawShow.hostVenue.image,
              address: rawShow.hostVenue.brandAddress || "Location unavailable",
            });
          }

          if (rawShow.promoter) {
            setPromoterData({
              id: rawShow.promoter._id,
              name: rawShow.promoter.brandName || rawShow.promoter.name,
              image: rawShow.promoter.image,
              handle: rawShow.promoter.handle || rawShow.promoter.email || "@brand",
            });
          }
        }
      } catch (error) {
        console.error("Error fetching show details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchShowDetails();
  }, [showId]);

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        setLoading(true);

        const response = await axios.get("/getMenuItemsPublic/getMenuItemsPublic", {
          params: { showId },
        });

        // Extract populated show details and menu items
        let items = Array.isArray(response.data) ? response.data : response.data?.data || [];

        // Filter available items only and display all regardless of categories
        items = items.filter((item) => item.isAvailable !== false);

        setMenuItems(items);
      } catch (error) {
        console.error("Failed to fetch menu items:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMenuItems();
  }, [showId]);

  const [selectedFlex, setSelectedFlex] = useState("doings"); // 'doings' | 'regular'
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
  const [isPhoneErrorVisible, setIsPhoneErrorVisible] = useState(false);

  const { setAuth } = useContext(AppContext);
  const { sub } = useAuth();

  const isLoggedIn = !!sub;

  const [signupCompleted, setSignupCompleted] = useState(false);

  // ── GOOGLE AUTH HOOK ──
  const {
    promptAsync: promptGoogleAsync,
    request: googleRequest,
    isAuthLoading: isGoogleLoading,
    errorMessage: googleErrorMessage,
    isErrorCompVisible: isGoogleErrorVisible,
    setIsErrorCompVisible: setIsGoogleErrorVisible,
  } = useGoogleAuth((strictLocalToken) => {
    setAuth({ accessToken: strictLocalToken, isProfileComplete: true });
    setSignupCompleted(true);
  });

  // ── APPLE AUTH HOOK ──
  const {
    handleAppleSuccess,
    handleAppleFailure,
    isAuthLoading: isAppleLoading,
    errorMessage: appleErrorMessage,
    isErrorCompVisible: isAppleErrorVisible,
    setIsErrorCompVisible: setIsAppleErrorVisible,
  } = useAppleAuth((strictLocalToken) => {
    setAuth({ accessToken: strictLocalToken, isProfileComplete: true });
    setSignupCompleted(true);
  });

  // Determine active error messaging states
  const activeErrorMessage = googleErrorMessage || appleErrorMessage || phoneErrorMessage;
  const isAnyErrorVisible = isGoogleErrorVisible || isAppleErrorVisible || isPhoneErrorVisible;
  const closeErrorModal = () => {
    setIsGoogleErrorVisible(false);
    setIsAppleErrorVisible(false);
    setIsPhoneErrorVisible(false);
  };

  const handleOtpPress = () => {
    setPhoneErrorMessage("Sorry, not available, continue with google or apple");
    setIsPhoneErrorVisible(true);
  };

  const handleGetOTP = async () => {
    // 1. Remove everything that isn't a number (removes spaces, +, dashes)
    let cleaned = whatsappNumber.replace(/\D/g, "");

    // 2. Remove ANY number of leading zeros (0, 00, 000...)
    cleaned = cleaned.replace(/^0+/, "");

    // 3. Remove leading 234 if it's now at the front
    if (cleaned.startsWith("234")) {
      cleaned = cleaned.slice(3);
    }

    // 4. Remove leading zero AGAIN (in case it was 234081...)
    cleaned = cleaned.replace(/^0+/, "");

    // Validation check
    if (cleaned.length !== 10) {
      // Show error...
      //console.log("number error");
      setPhoneErrorMessage("You might have entered a wrong number");
      setIsPhoneErrorVisible(true);
      // setErrorMessage("You might have entered a wrong number");
      // setIsErrorCompVisible(true);
      return;
    }

    // 3. Final standardized format
    const finalPhoneNumber = `234${cleaned}`;
    console.log("FinalPhoneNumberYTT", finalPhoneNumber);
    try {
      setIsLoadingOTP(true);
      const response = await axios.post("/requestOtp/requestOtp", {
        phoneNumber: finalPhoneNumber,
      });

      if (response.status === 200) {
        navigation.navigate("anonymousOnboardingOTP", {
          whatsappNumber: finalPhoneNumber,
          mode: "preShow",
        });
      }
    } catch (error) {
      console.error("Error searching phone:", error);
    } finally {
      setIsLoadingOTP(false);
    }
  };

  const handleNextStep = () => {
    const payload = {
      isReserved: true,
      currentTier: selectedFlex,
      tableAnonymity: isAnonymous,
      promoterId: promoterData?.id,
      venueId: venueData?.id,
      showId: showData?.id,
    };

    console.log("Selected Configuration:", payload);
    navigation.navigate("brandMenuPrepaid", payload);
  };

  const isAuthLoading = isGoogleLoading || isAppleLoading;

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Colours.PRIMARY || "#2A65F0"} />
      </View>
    );
  }

  // const activeMenuItems = showData?.menuItems?.length ? showData.menuItems : sampleMenuItems;
  const activeMenuItems = menuItems ? menuItems : sampleMenuItems;

  return (
    <SafeAreaView style={[styles.safeArea, webSafeArea]}>
      <View style={styles.topHeaderContainer3}>
        <TouchableOpacity
          onPress={() => navigation.canGoBack() && navigation.goBack()}
          activeOpacity={0.7}
        >
          <Ionicons name="arrow-back" size={dynamicSize(22)} color="#1A1A1A" />
        </TouchableOpacity>
        <Text style={styles.headerTitleTxt} numberOfLines={1}>
          {showData?.title || "Choose Spend Mode"}
        </Text>
      </View>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={styles.scrollWrapper}
        showsVerticalScrollIndicator={false}
      >
        {/* ── SHOW COVER & DETAILS CARD ── */}
        <View style={styles.showCardContainer}>
          <Image
            source={{
              uri:
                showData?.coverImage ||
                "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=1000&auto=format&fit=crop",
            }}
            style={styles.showCoverImage}
            contentFit="cover"
            cachePolicy="disk"
          />
          <View style={styles.showCardBody}>
            <View style={styles.showLiveTagRow}>
              <View style={styles.liveIndicatorDot} />
              <Text style={styles.showLiveTagText}>UPCOMING SHOW</Text>
            </View>

            <Text style={styles.showTitle} numberOfLines={2}>
              {showData?.title || "VIP Night Out & Music Festival"}
            </Text>

            <View style={styles.showInfoMetaRow}>
              <View style={styles.showInfoItem}>
                <Ionicons name="calendar-outline" size={dynamicSize(13)} color="#D48300" />
                <Text style={styles.showInfoText}>{showData?.date || "Sat, 24 Oct"}</Text>
              </View>
              <View style={styles.showMetaDivider} />
              <View style={styles.showInfoItem}>
                <Ionicons name="time-outline" size={dynamicSize(13)} color="#D48300" />
                <Text style={styles.showInfoText}>{showData?.time || "10:00 PM"}</Text>
              </View>
            </View>

            {showData?.description ? (
              <Text style={styles.showDescription} numberOfLines={2}>
                {showData.description}
              </Text>
            ) : null}
          </View>
        </View>

        {/* COMBINED HERO SECTION: PROMOTER & VENUE BRAND CARDS */}
        <View style={styles.brandingWrapper}>
          <View style={styles.brandHeroCard}>
            <Image
              source={{ uri: promoterData?.image || "https://placehold.co/150" }}
              style={styles.brandImage}
              contentFit="cover"
              cachePolicy="disk"
            />
            <View style={styles.brandMetaInfo}>
              <View style={styles.badgeRow}>
                <Text style={styles.promoterTag}>HEADLINER BRAND</Text>
              </View>
              <Text style={styles.nameTxt} numberOfLines={1}>
                {promoterData?.name || "Promoter Name"}
              </Text>
              <Text style={styles.emailTxt} numberOfLines={1}>
                {promoterData?.handle || "@promoter_handle"}
              </Text>
            </View>
          </View>

          <View style={styles.venueHeroCard}>
            <Image
              source={{ uri: venueData?.image || "https://placehold.co/150" }}
              style={styles.venueImage}
              contentFit="cover"
              cachePolicy="disk"
            />
            <View style={styles.brandMetaInfo}>
              <Text style={styles.venueLabel}>HOST VENUE</Text>
              <Text style={styles.venueNameTxt} numberOfLines={1}>
                {venueData?.name || "Venue / Club Name"}
              </Text>
              <Text style={styles.emailTxt} numberOfLines={1}>
                {venueData?.address || "Venue Location Details"}
              </Text>
            </View>
          </View>
        </View>

        {/* VENUE ORDERING NOTICE CARD */}
        <View style={styles.venueNoticeCard}>
          <View style={styles.noticeIconHeader}>
            <View style={styles.noticeIconBadge}>
              <MaterialCommunityIcons
                name="qrcode-scan"
                size={dynamicSize(24)}
                color={Colours.PRIMARY || "#2A65F0"}
              />
            </View>
            <View style={styles.noticeTag}>
              <Text style={styles.noticeTagText}>POWERED BY MEEZA</Text>
            </View>
          </View>

          <Text style={styles.noticeTitle}>In-Venue Digital Experience</Text>

          <Text style={styles.noticeBodyText}>
            All food, drink, and table service orders for this show will be seamlessly handled via{" "}
            <Text style={styles.boldHighlight}>Meeza</Text>.
          </Text>

          <View style={styles.noticeStepList}>
            <View style={styles.noticeStepRow}>
              <View style={styles.stepDot}>
                <Text style={styles.stepDotNumber}>1</Text>
              </View>
              <Text style={styles.stepText}>
                Upon arrival, your waiter will present a unique QR code at your table.
              </Text>
            </View>

            <View style={styles.noticeStepRow}>
              <View style={styles.stepDot}>
                <Text style={styles.stepDotNumber}>2</Text>
              </View>
              <Text style={styles.stepText}>
                Scan to instantly unlock the event menu, order directly, and manage billing.
              </Text>
            </View>
          </View>

          <View style={styles.calloutBox}>
            <Ionicons
              name="sparkles"
              size={dynamicSize(16)}
              color="#D48300"
              style={styles.calloutIcon}
            />
            <Text style={styles.calloutText}>
              Create your Meeza account ahead of time to get geared up for an unforgettable night!
            </Text>
          </View>
        </View>

        {/* ── DOINGS MODE EXPLANATION CARD ── */}
        <View style={styles.doingsExplanationContainer}>
          <View style={styles.doingsHeaderRow}>
            <View style={styles.doingsBadgeIcon}>
              <MaterialCommunityIcons name="crown" size={dynamicSize(18)} color="#FF9900" />
            </View>
            <Text style={styles.doingsHeaderTitle}>Doings Mode Perks</Text>
          </View>
          <Text style={styles.doingsHeaderSub}>
            Spending in Doings mode gives your table the Doings badge and places your table at the
            very top of the Doings trend.
          </Text>
        </View>

        {/* ── EVENT MENU SHOWCASE ── */}
        <View style={styles.menuContainerSection}>
          <Text style={styles.sectionLabel}>EVENT MENU & PRICING</Text>

          {activeMenuItems.map((item, index) => (
            <View key={item._id || index} style={styles.menuItemCard}>
              {item.image ? (
                <Image
                  source={{ uri: item.image }}
                  style={styles.menuItemImage}
                  contentFit="cover"
                  cachePolicy="disk"
                />
              ) : null}
              <View style={styles.menuItemContent}>
                <Text style={styles.menuItemName} numberOfLines={1}>
                  {item.name}
                </Text>
                {item.description ? (
                  <Text style={styles.menuItemDesc} numberOfLines={2}>
                    {item.description}
                  </Text>
                ) : null}

                <View style={styles.menuPriceRow}>
                  <View style={styles.priceColumn}>
                    <Text style={styles.priceLabel}>Regular Mode</Text>
                    <Text style={styles.regularPriceVal}>
                      ₦{(item.regularPrice ?? 0).toLocaleString()}
                    </Text>
                  </View>
                  <View style={styles.priceDivider} />
                  <View style={styles.priceColumn}>
                    <Text style={[styles.priceLabel, { color: "#FF9900" }]}>Doings Mode</Text>
                    <Text style={styles.doingsPriceVal}>
                      ₦{(item.doingsPrice ?? 0).toLocaleString()}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          ))}
        </View>

        {/* ── SIGNUP & GET OTP SECTION ── */}
        {/* <View style={styles.signupSectionContainer}>
          <Text style={styles.sectionLabel}>GET STARTED WITH MEEZA</Text>

          <TouchableOpacity style={styles.socialButtonGoogle} activeOpacity={0.8}>
            <FontAwesome
              name="google"
              size={dynamicSize(16)}
              color="#DB4437"
              style={{ marginRight: 10 }}
            />
            <Text style={styles.socialButtonTextGoogle}>Continue with Google</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.socialButtonApple} activeOpacity={0.8}>
            <FontAwesome
              name="apple"
              size={dynamicSize(18)}
              color="#FFF"
              style={{ marginRight: 10 }}
            />
            <Text style={styles.socialButtonTextApple}>Continue with Apple</Text>
          </TouchableOpacity>

          <View style={styles.dividerRow}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>OR WITH WHATSAPP</Text>
            <View style={styles.dividerLine} />
          </View>

          <View style={styles.whatsappInputWrapper}>
            <View style={styles.phoneInputRow}>
              <View style={styles.countryCodeBox}>
                <Text style={styles.countryCodeTxt}>+234</Text>
              </View>
              <TextInput
                style={styles.whatsappTextInput}
                placeholder="WhatsApp Phone Number"
                placeholderTextColor="#999"
                keyboardType="phone-pad"
                value={whatsappNumber}
                onChangeText={setWhatsAppNumber}
              />
            </View>
            <TouchableOpacity style={styles.getOtpButton} activeOpacity={0.8}>
              <Text style={styles.getOtpButtonText}>Get OTP</Text>
            </TouchableOpacity>
          </View>
        </View> */}

        {isAuthLoading ? (
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="small" color={Colours.PRIMARY} />
            <Text style={styles.loadingText}>Authenticating...</Text>
          </View>
        ) : (
          <>
            {/* {!signupCompleted && ( */}
            {!isLoggedIn && (
              <View style={styles.signupSectionContainer}>
                <Text style={styles.sectionLabel}>GET STARTED WITH MEEZA</Text>

                <TouchableOpacity
                  style={[
                    styles.socialButtonGoogle,
                    (!googleRequest || isGoogleLoading) && { opacity: 0.6 },
                  ]}
                  activeOpacity={0.8}
                  disabled={!googleRequest || isGoogleLoading}
                  // onPress={() => promptAsync()}
                  onPress={() => promptGoogleAsync()}
                >
                  <FontAwesome
                    name="google"
                    size={dynamicSize(16)}
                    color="#DB4437"
                    style={{ marginRight: 10 }}
                  />
                  <Text style={styles.socialButtonTextGoogle}>
                    {isGoogleLoading ? "Connecting..." : "Continue with Google"}
                  </Text>
                </TouchableOpacity>

                <AppleSignin
                  authOptions={{
                    // clientId: "com.mexa.pwa", // Your registered Apple Services
                    clientId: "com.mexxzia.pwa",
                    scope: "email name",
                    redirectURI: "https://app.getmeeza.com/",
                    // redirectURI: "https://getmeeza.com/prollix/appleAuth/appleAuth", // Must match Apple Developer Portal
                    usePopup: true, // Prevents page reload so cart state remains intact
                  }}
                  onSuccess={handleAppleSuccess}
                  onError={handleAppleFailure}
                  render={(props) => (
                    <TouchableOpacity
                      style={styles.socialButtonApple}
                      activeOpacity={0.8}
                      disabled={isAppleLoading}
                      onPress={props.onClick}
                    >
                      <FontAwesome
                        name="apple"
                        size={dynamicSize(18)}
                        color="#FFF"
                        style={{ marginRight: 10 }}
                      />
                      <Text style={styles.socialButtonTextApple}>
                        {isAppleLoading ? "Connecting..." : "Continue with Apple"}
                      </Text>
                    </TouchableOpacity>
                  )}
                />

                <View style={styles.dividerRow}>
                  <View style={styles.dividerLine} />
                  <Text style={styles.dividerText}>OR WITH WHATSAPP</Text>
                  <View style={styles.dividerLine} />
                </View>

                <View style={styles.whatsappInputWrapper}>
                  <View style={styles.phoneInputRow}>
                    <View style={styles.countryCodeBox}>
                      <Text style={styles.countryCodeTxt}>+234</Text>
                    </View>
                    <TextInput
                      style={styles.whatsappTextInput}
                      placeholder="WhatsApp Phone Number"
                      placeholderTextColor="#999"
                      keyboardType="phone-pad"
                      value={whatsappNumber}
                      onChangeText={setWhatsAppNumber}
                    />
                  </View>
                  <TouchableOpacity
                    disabled={isLoadingOTP}
                    style={[styles.getOtpButton]}
                    activeOpacity={0.8}
                    onPress={handleGetOTP}
                  >
                    {isLoadingOTP ? (
                      <ActivityIndicator size="small" color="white" />
                    ) : (
                      <Text style={styles.getOtpButtonText}>Get OTP</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </>
        )}
      </ScrollView>

      {/* FOOTER STICKY BAR */}

      {/* FOOTER STICKY BAR */}
      <View style={styles.footerStickyBar}>
        {/* {signupCompleted && ( */}
        {isLoggedIn && (
          <TouchableOpacity
            style={styles.proceedBtn}
            activeOpacity={0.85}
            onPress={() => navigation.navigate("main")}
          >
            <Text style={styles.proceedBtnText}>Go to Home Page</Text>
            <Ionicons name="home" size={dynamicSize(18)} color="white" />
          </TouchableOpacity>
        )}
      </View>

      {/* <Modal visible={isErrorCompVisible} transparent={true} animationType="fade">
        <ErrorAlertComp message={errorMessage} onClose={() => setIsErrorCompVisible(false)} />
      </Modal> */}
      <Modal visible={isAnyErrorVisible} transparent={true} animationType="fade">
        <ErrorAlertComp message={activeErrorMessage} onClose={closeErrorModal} />
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#FAFAFC",
  },
  topHeaderContainer3: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: dynamicSpacing(16),
    paddingVertical: dynamicSpacing(12),
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#EAEAEF",
    gap: dynamicSpacing(12),
  },
  headerTitleTxt: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(16),
    color: "#1A1A1A",
    flex: 1,
  },
  scrollWrapper: {
    padding: dynamicSpacing(20),
    paddingBottom: dynamicSpacing(130),
  },

  loaderContainer: {
    paddingVertical: dynamicSpacing(20),
    alignItems: "center",
  },

  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FAFAFC",
  },

  // SHOW COVER CONTAINER & DETAILS
  showCardContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: dynamicSize(18),
    overflow: "hidden",
    marginBottom: dynamicSpacing(16),
    borderWidth: 1,
    borderColor: "#EAEAEF",
    elevation: 3,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
  },
  showCoverImage: {
    width: "100%",
    height: dynamicSize(150),
    backgroundColor: "#F0F0F5",
  },
  showCardBody: {
    padding: dynamicSpacing(14),
  },
  showLiveTagRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: dynamicSpacing(6),
    gap: dynamicSpacing(6),
  },
  liveIndicatorDot: {
    width: dynamicSize(6),
    height: dynamicSize(6),
    borderRadius: dynamicSize(3),
    backgroundColor: "#FF9900",
  },
  showLiveTagText: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(10),
    color: "#FF9900",
    letterSpacing: 0.8,
  },
  showTitle: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(18),
    color: "#1A1A1A",
    lineHeight: dynamicFontSize(22),
    marginBottom: dynamicSpacing(8),
  },
  showInfoMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFBF4",
    paddingHorizontal: dynamicSpacing(10),
    paddingVertical: dynamicSpacing(6),
    borderRadius: dynamicSize(8),
    alignSelf: "flex-start",
    marginBottom: dynamicSpacing(8),
    borderWidth: 1,
    borderColor: "#FFE6C2",
  },
  showInfoItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: dynamicSpacing(4),
  },
  showInfoText: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(12),
    color: "#D48300",
  },
  showMetaDivider: {
    width: 1,
    height: dynamicSize(12),
    backgroundColor: "#FFD699",
    marginHorizontal: dynamicSpacing(10),
  },
  showDescription: {
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(12),
    color: "#666",
    lineHeight: dynamicFontSize(17),
  },

  // HERO BRANDING SECTION (Promoter + Venue)
  brandingWrapper: {
    marginBottom: dynamicSpacing(20),
    gap: dynamicSpacing(10),
  },
  brandHeroCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "white",
    padding: dynamicSpacing(14),
    borderRadius: dynamicSize(16),
    elevation: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    borderWidth: 1,
    borderColor: "#EAEAEF",
  },
  venueHeroCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F4F4F8",
    padding: dynamicSpacing(12),
    borderRadius: dynamicSize(14),
    borderWidth: 1,
    borderColor: "#EAEAEF",
  },
  brandImage: {
    width: dynamicSize(56),
    height: dynamicSize(56),
    borderRadius: dynamicSize(28),
    backgroundColor: "#F5F5F7",
  },
  venueImage: {
    width: dynamicSize(42),
    height: dynamicSize(42),
    borderRadius: dynamicSize(10),
    backgroundColor: "#E5E5EA",
  },
  brandMetaInfo: {
    flex: 1,
    marginLeft: dynamicSpacing(12),
    justifyContent: "center",
  },
  badgeRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: dynamicSpacing(2),
  },
  promoterTag: {
    backgroundColor: "#E8F0FE",
    color: Colours.PRIMARY || "#2A65F0",
    fontSize: dynamicFontSize(9),
    fontFamily: "Sen-Bold",
    paddingHorizontal: dynamicSpacing(6),
    paddingVertical: dynamicSpacing(2),
    borderRadius: dynamicSize(4),
  },
  venueLabel: {
    color: "#888",
    fontSize: dynamicFontSize(9),
    fontFamily: "Sen-Bold",
    letterSpacing: 0.5,
    marginBottom: dynamicSpacing(2),
  },
  nameTxt: {
    color: Colours.DARKGRAY || "#1A1A1A",
    fontSize: dynamicFontSize(16),
    fontFamily: "Sen-Bold",
  },
  venueNameTxt: {
    color: "#333",
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
  },
  emailTxt: {
    color: "#666",
    fontSize: dynamicFontSize(12),
    fontFamily: "Sen-Regular",
  },

  // VENUE ORDERING NOTICE CARD
  venueNoticeCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: dynamicSize(18),
    padding: dynamicSpacing(18),
    marginBottom: dynamicSpacing(20),
    borderWidth: 1,
    borderColor: "#EAEAEF",
    elevation: 3,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
  },
  noticeIconHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: dynamicSpacing(12),
  },
  noticeIconBadge: {
    width: dynamicSize(46),
    height: dynamicSize(46),
    borderRadius: dynamicSize(12),
    backgroundColor: "#EEF4FF",
    justifyContent: "center",
    alignItems: "center",
  },
  noticeTag: {
    backgroundColor: "#E8F0FE",
    paddingHorizontal: dynamicSpacing(10),
    paddingVertical: dynamicSpacing(4),
    borderRadius: dynamicSize(6),
  },
  noticeTagText: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(10),
    color: Colours.PRIMARY || "#2A65F0",
    letterSpacing: 0.8,
  },
  noticeTitle: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(18),
    color: "#1A1A1A",
    marginBottom: dynamicSpacing(6),
  },
  noticeBodyText: {
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(13),
    color: "#555555",
    lineHeight: dynamicFontSize(19),
    marginBottom: dynamicSpacing(16),
  },
  boldHighlight: {
    fontFamily: "Sen-Bold",
    color: Colours.PRIMARY || "#2A65F0",
  },
  noticeStepList: {
    gap: dynamicSpacing(12),
    marginBottom: dynamicSpacing(16),
  },
  noticeStepRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: dynamicSpacing(10),
  },
  stepDot: {
    width: dynamicSize(20),
    height: dynamicSize(20),
    borderRadius: dynamicSize(10),
    backgroundColor: "#F0F0F5",
    justifyContent: "center",
    alignItems: "center",
    marginTop: dynamicSpacing(1),
  },
  stepDotNumber: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(11),
    color: "#444444",
  },
  stepText: {
    flex: 1,
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(12),
    color: "#4A4A4A",
    lineHeight: dynamicFontSize(17),
  },
  calloutBox: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFBF4",
    padding: dynamicSpacing(12),
    borderRadius: dynamicSize(12),
    borderWidth: 1,
    borderColor: "#FFE6C2",
  },
  calloutIcon: {
    marginRight: dynamicSpacing(8),
  },
  calloutText: {
    flex: 1,
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(12),
    color: "#D48300",
    lineHeight: dynamicFontSize(17),
  },

  // DOINGS EXPLANATION CONTAINER
  doingsExplanationContainer: {
    backgroundColor: "#FFFBF4",
    borderRadius: dynamicSize(16),
    padding: dynamicSpacing(16),
    marginBottom: dynamicSpacing(20),
    borderWidth: 1,
    borderColor: "#FFE6C2",
  },
  doingsHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: dynamicSpacing(8),
    gap: dynamicSpacing(8),
  },
  doingsBadgeIcon: {
    width: dynamicSize(32),
    height: dynamicSize(32),
    borderRadius: dynamicSize(8),
    backgroundColor: "#FFF3E0",
    justifyContent: "center",
    alignItems: "center",
  },
  doingsHeaderTitle: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(15),
    color: "#D48300",
  },
  doingsHeaderSub: {
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(13),
    color: "#664D00",
    lineHeight: dynamicFontSize(19),
  },

  // MENU CONTAINER & ITEMS
  sectionLabel: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(11),
    color: "#888",
    letterSpacing: 1.2,
    marginBottom: dynamicSpacing(12),
  },
  menuContainerSection: {
    marginBottom: dynamicSpacing(20),
  },
  menuItemCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: dynamicSize(16),
    overflow: "hidden",
    marginBottom: dynamicSpacing(12),
    borderWidth: 1,
    borderColor: "#EAEAEF",
    flexDirection: "row",
    elevation: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 5,
  },
  menuItemImage: {
    width: dynamicSize(110),
    height: "100%",
    backgroundColor: "#F0F0F5",
  },
  menuItemContent: {
    flex: 1,
    padding: dynamicSpacing(12),
    justifyContent: "space-between",
  },
  menuItemName: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(14),
    color: "#1A1A1A",
    marginBottom: dynamicSpacing(4),
  },
  menuItemDesc: {
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(12),
    color: "#666",
    lineHeight: dynamicFontSize(16),
    marginBottom: dynamicSpacing(8),
  },
  menuPriceRow: {
    flexDirection: "row",
    alignItems: "center",
    borderTopWidth: 1,
    borderTopColor: "#F4F4F8",
    paddingTop: dynamicSpacing(8),
  },
  priceColumn: {
    flex: 1,
  },
  priceDivider: {
    width: 1,
    height: dynamicSize(24),
    backgroundColor: "#EAEAEF",
    marginHorizontal: dynamicSpacing(8),
  },
  priceLabel: {
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(10),
    color: "#888",
    marginBottom: 2,
  },
  regularPriceVal: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(13),
    color: "#333",
  },
  doingsPriceVal: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(13),
    color: "#FF9900",
  },

  // SIGNUP SECTION
  signupSectionContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: dynamicSize(18),
    padding: dynamicSpacing(18),
    borderWidth: 1,
    borderColor: "#EAEAEF",
    marginBottom: dynamicSpacing(20),
    elevation: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 5,
  },
  socialButtonGoogle: {
    flexDirection: "row",
    height: dynamicSize(50),
    borderRadius: dynamicSize(12),
    borderWidth: 1,
    borderColor: "#EAEAEF",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: dynamicSpacing(10),
    backgroundColor: "#FFF",
  },
  socialButtonTextGoogle: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
    color: "#333",
  },
  socialButtonApple: {
    flexDirection: "row",
    height: dynamicSize(50),
    borderRadius: dynamicSize(12),
    backgroundColor: "#1A1A1A",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: dynamicSpacing(16),
  },
  socialButtonTextApple: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
    color: "#FFF",
  },
  dividerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: dynamicSpacing(16),
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: "#EAEAEF",
  },
  dividerText: {
    fontSize: dynamicFontSize(10),
    fontFamily: "Sen-Bold",
    color: "#888",
    paddingHorizontal: dynamicSpacing(10),
    letterSpacing: 0.5,
  },
  whatsappInputWrapper: {
    gap: dynamicSpacing(10),
  },
  phoneInputRow: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#EAEAEF",
    borderRadius: dynamicSize(12),
    overflow: "hidden",
    backgroundColor: "#FAFAFC",
    height: dynamicSize(50),
    alignItems: "center",
  },
  countryCodeBox: {
    paddingHorizontal: dynamicSpacing(14),
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F0F0F5",
    height: "100%",
    borderRightWidth: 1,
    borderRightColor: "#EAEAEF",
  },
  countryCodeTxt: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
    color: "#333",
  },
  whatsappTextInput: {
    flex: 1,
    paddingHorizontal: dynamicSpacing(12),
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Regular",
    color: "#1A1A1A",
    height: "100%",
  },
  getOtpButton: {
    height: dynamicSize(50),
    borderRadius: dynamicSize(12),
    backgroundColor: Colours.PRIMARY || "#2A65F0",
    justifyContent: "center",
    alignItems: "center",
  },
  getOtpButtonText: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
    color: "#FFF",
  },

  // BOTTOM STICKY BAR
  footerStickyBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "white",
    paddingHorizontal: dynamicSpacing(20),
    paddingTop: dynamicSpacing(15),
    paddingBottom: Platform.OS === "ios" ? dynamicSpacing(35) : dynamicSpacing(20),
    borderTopWidth: 1,
    borderColor: "#EAEAEF",
  },
  proceedBtn: {
    backgroundColor: Colours.PRIMARY || "#2A65F0",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    height: dynamicSize(54),
    borderRadius: dynamicSize(15),
    gap: dynamicSpacing(10),
  },
  proceedBtnText: {
    color: "white",
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(16),
  },
});

export default ChooseSpendModePrepaid;

// import React, { useContext, useEffect, useState } from "react";
// import {
//   View,
//   Text,
//   Switch,
//   TouchableOpacity,
//   ScrollView,
//   StyleSheet,
//   PixelRatio,
//   Dimensions,
//   Platform,
//   Modal,
//   ActivityIndicator,
// } from "react-native";
// import { Image } from "expo-image";
// import { MaterialCommunityIcons, Ionicons } from "@expo/vector-icons";
// import Colours from "../../../utils/Colours";
// import TopHeaderComponent from "../../aTopHeader/TopHeaderComponent";
// import { useNavigation, useRoute } from "@react-navigation/native";
// import { useWebSafeArea } from "../../../hooks/useWebHeights";
// import ErrorAlertComp from "../../../alertComp/ErrorAlertComp";
// import { SafeAreaView } from "react-native-safe-area-context";
// import axios from "../../../api/axios";

// const { width, height } = Dimensions.get("window");

// // --- RESPONSIVE TOOLKIT ---
// const BASE_WIDTH = 375;
// const BASE_HEIGHT = 812;

// const dynamicFontSize = (size) => {
//   const scaleFactor = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
//   return Math.round(PixelRatio.roundToNearestPixel(size * scaleFactor));
// };
// const dynamicSpacing = (size) => (width / BASE_WIDTH) * size;
// const dynamicSize = (size) => (width / BASE_WIDTH) * size;

// const ChooseSpendModePrepaid = () => {
//   const navigation = useNavigation();
//   const route = useRoute();
//   const webSafeArea = useWebSafeArea();

//   const { showId } = route.params || {};

//   // Route Params / Props fallback (Added showData parameter)
//   //   const { promoterData, venueData, showData } = route.params || {};
//   const [loading, setLoading] = useState(true);
//   const [showData, setShowData] = useState(null);
//   const [promoterData, setPromoterData] = useState(null);
//   const [venueData, setVenueData] = useState(null);
//   useEffect(() => {
//     const fetchShowDetails = async () => {
//       if (!showId) {
//         setLoading(false);
//         return;
//       }

//       try {
//         setLoading(true);
//         // Make Axios request to backend endpoint
//         const response = await axios.get(`/get_a_show/get_a_show/${showId}`);

//         if (response?.data?.success) {
//           const rawShow = response.data.data;

//           // Format show details
//           setShowData({
//             id: rawShow._id,
//             title: rawShow.caption || "Special Event",
//             coverImage: rawShow.image,
//             date: rawShow.startDate
//               ? new Date(rawShow.startDate).toLocaleDateString("en-US", {
//                   weekday: "short",
//                   day: "numeric",
//                   month: "short",
//                 })
//               : "TBD",
//             time: rawShow.startDate
//               ? new Date(rawShow.startDate).toLocaleTimeString("en-US", {
//                   hour: "2-digit",
//                   minute: "2-digit",
//                 })
//               : "TBD",
//             description: rawShow.caption,
//             menuItems: rawShow.menuItems || [],
//           });

//           // Format populated Host Venue
//           if (rawShow.hostVenue) {
//             setVenueData({
//               id: rawShow.hostVenue._id,
//               name: rawShow.hostVenue.brandName || rawShow.hostVenue.name,
//               image: rawShow.hostVenue.image,
//               address: rawShow.hostVenue.brandAddress || "Location unavailable",
//             });
//           }

//           // Format populated Promoter
//           if (rawShow.promoter) {
//             setPromoterData({
//               id: rawShow.promoter._id,
//               name: rawShow.promoter.brandName || rawShow.promoter.name,
//               image: rawShow.promoter.image,
//               handle: rawShow.promoter.handle || rawShow.promoter.email || "@brand",
//             });
//           }
//         }
//       } catch (error) {
//         console.error("Error fetching show details:", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchShowDetails();
//   }, [showId]);

//   // Component State
//   const [selectedFlex, setSelectedFlex] = useState("doings"); // 'doings' | 'regular'
//   const [isAnonymous, setIsAnonymous] = useState(false);
//   const [isErrorCompVisible, setIsErrorCompVisible] = useState(false);
//   const [errorMessage, setErrorMessage] = useState("");

//   const handleBackNavigation = () => {
//     if (navigation.canGoBack()) {
//       navigation.goBack();
//     }
//   };

//   const proceedToSignup = () => {
//     window.location.href = "https://app.getmeeza.com";
//   };

//   const handleNextStep = () => {
//     // Submit selection and navigate to menu/checkout
//     const payload = {
//       isReserved: true,
//       currentTier: selectedFlex,
//       tableAnonymity: isAnonymous,
//       promoterId: promoterData?.id,
//       venueId: venueData?.id,
//       showId: showData?.id,
//     };

//     console.log("Selected Configuration:", payload);
//     navigation.navigate("brandMenuPrepaid", payload);
//   };

//   if (loading) {
//     return (
//       <View style={styles.loadingContainer}>
//         <ActivityIndicator size="large" color={Colours.PRIMARY} />
//       </View>
//     );
//   }

//   return (
//     <SafeAreaView style={[styles.safeArea, webSafeArea]}>
//       <View style={[styles.topHeaderContainer3]}>
//         <Text style={{ fontSize: 18, color: Colours.DARKGRAY, fontFamily: "Sen-Bold" }}>
//           {showData?.title}
//         </Text>
//       </View>
//       <ScrollView
//         style={{ flex: 1 }}
//         contentContainerStyle={styles.scrollWrapper}
//         showsVerticalScrollIndicator={false}
//       >
//         {/* ── SHOW COVER & DETAILS CARD ── */}
//         <View style={styles.showCardContainer}>
//           <Image
//             source={{
//               uri:
//                 showData?.coverImage ||
//                 "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=1000&auto=format&fit=crop",
//             }}
//             style={styles.showCoverImage}
//             contentFit="cover"
//             cachePolicy="disk"
//           />
//           <View style={styles.showCardBody}>
//             <View style={styles.showLiveTagRow}>
//               <View style={styles.liveIndicatorDot} />
//               <Text style={styles.showLiveTagText}>UPCOMING SHOW</Text>
//             </View>

//             <Text style={styles.showTitle} numberOfLines={2}>
//               {showData?.title || "VIP Night Out & Music Festival"}
//             </Text>

//             {/* Event Time & Date Row */}
//             <View style={styles.showInfoMetaRow}>
//               <View style={styles.showInfoItem}>
//                 <Ionicons name="calendar-outline" size={dynamicSize(14)} color="#E67E00" />
//                 <Text style={styles.showInfoText}>{showData?.date || "Sat, 24 Oct"}</Text>
//               </View>
//               <View style={styles.showMetaDivider} />
//               <View style={styles.showInfoItem}>
//                 <Ionicons name="time-outline" size={dynamicSize(14)} color="#E67E00" />
//                 <Text style={styles.showInfoText}>{showData?.time || "10:00 PM"}</Text>
//               </View>
//             </View>

//             {showData?.description ? (
//               <Text style={styles.showDescription} numberOfLines={2}>
//                 {showData.description}
//               </Text>
//             ) : null}
//           </View>
//         </View>

//         {/* COMBINED HERO SECTION: PROMOTER & VENUE BRAND CARDS */}
//         <View style={styles.brandingWrapper}>
//           {/* Promoter Card */}
//           <View style={styles.brandHeroCard}>
//             <Image
//               source={{ uri: promoterData?.image || "https://placehold.co/150" }}
//               style={styles.brandImage}
//               contentFit="cover"
//               cachePolicy="disk"
//             />
//             <View style={styles.brandMetaInfo}>
//               <View style={styles.badgeRow}>
//                 <Text style={styles.promoterTag}>HEADLINER BRAND</Text>
//               </View>
//               <Text style={styles.nameTxt} numberOfLines={1}>
//                 {promoterData?.name || "Promoter Name"}
//               </Text>
//               <Text style={styles.emailTxt} numberOfLines={1}>
//                 {promoterData?.handle || "@promoter_handle"}
//               </Text>
//             </View>
//           </View>

//           {/* Linked Host Venue Card */}
//           <View style={styles.venueHeroCard}>
//             <Image
//               source={{ uri: venueData?.image || "https://placehold.co/150" }}
//               style={styles.venueImage}
//               contentFit="cover"
//               cachePolicy="disk"
//             />
//             <View style={styles.brandMetaInfo}>
//               <Text style={styles.venueLabel}>HOST VENUE</Text>
//               <Text style={styles.venueNameTxt} numberOfLines={1}>
//                 {venueData?.name || "Venue / Club Name"}
//               </Text>
//               <Text style={styles.emailTxt} numberOfLines={1}>
//                 {venueData?.address || "Venue Location Details"}
//               </Text>
//             </View>
//           </View>
//         </View>

//         {/* VENUE ORDERING NOTICE CARD */}
//         <View style={styles.venueNoticeCard}>
//           <View style={styles.noticeIconHeader}>
//             <View style={styles.noticeIconBadge}>
//               <MaterialCommunityIcons
//                 name="qrcode-scan"
//                 size={dynamicSize(24)}
//                 color={Colours.PRIMARY || "#2A65F0"}
//               />
//             </View>
//             <View style={styles.noticeTag}>
//               <Text style={styles.noticeTagText}>POWERED BY MEEZA</Text>
//             </View>
//           </View>

//           <Text style={styles.noticeTitle}>In-Venue Digital Experience</Text>

//           <Text style={styles.noticeBodyText}>
//             All food, drink, and table service orders for this show will be seamlessly handled via{" "}
//             <Text style={styles.boldHighlight}>Meeza</Text>.
//           </Text>

//           <View style={styles.noticeStepList}>
//             <View style={styles.noticeStepRow}>
//               <View style={styles.stepDot}>
//                 <Text style={styles.stepDotNumber}>1</Text>
//               </View>
//               <Text style={styles.stepText}>
//                 Upon arrival, your waiter will present a unique QR code at your table.
//               </Text>
//             </View>

//             <View style={styles.noticeStepRow}>
//               <View style={styles.stepDot}>
//                 <Text style={styles.stepDotNumber}>2</Text>
//               </View>
//               <Text style={styles.stepText}>
//                 Scan to instantly unlock the event menu, order directly, and manage billing.
//               </Text>
//             </View>
//           </View>

//           <View style={styles.calloutBox}>
//             <Ionicons
//               name="sparkles"
//               size={dynamicSize(18)}
//               color="#FF9900"
//               style={styles.calloutIcon}
//             />
//             <Text style={styles.calloutText}>
//               Create your Meeza account ahead of time to get geared up for an unforgettable night!
//             </Text>
//           </View>
//         </View>

//       </ScrollView>

//       {/* FIXED BOTTOM SUBMIT BUTTON */}
//       {/* <View style={styles.footerStickyBar}>
//         <TouchableOpacity style={styles.proceedBtn} onPress={proceedToSignup}>
//           <Text style={styles.proceedBtnText}>Proceed to Signup</Text>
//           <Ionicons name="arrow-forward" size={dynamicSize(18)} color="white" />
//         </TouchableOpacity>
//       </View> */}

//       <Modal visible={isErrorCompVisible} transparent={true} animationType="fade">
//         <ErrorAlertComp message={errorMessage} onClose={() => setIsErrorCompVisible(false)} />
//       </Modal>
//     </SafeAreaView>
//   );
// };

// export default ChooseSpendModePrepaid;

// // --- STYLING SPEC SHEET ---
// const styles = StyleSheet.create({
//   safeArea: {
//     flex: 1,
//     backgroundColor: "#FAFAFC",
//   },
//   topHeaderContainer3: {
//     display: "flex",
//     flexDirection: "row",
//     alignItems: "center",
//     padding: 15,
//     paddingTop: dynamicSpacing(30),
//     gap: 15,
//     flexWrap: "wrap",
//   },
//   scrollWrapper: {
//     padding: dynamicSpacing(20),
//     paddingBottom: dynamicSpacing(130),
//   },

//   loadingContainer: {
//     flex: 1,
//     justifyContent: "center",
//     alignItems: "center",
//     backgroundColor: "#FAFAFC",
//   },

//   // SHOW COVER CONTAINER & DETAILS
//   showCardContainer: {
//     backgroundColor: "#FFFFFF",
//     borderRadius: dynamicSize(18),
//     overflow: "hidden",
//     marginBottom: dynamicSpacing(16),
//     borderWidth: 1,
//     borderColor: "#EAEAEF",
//     elevation: 3,
//     shadowColor: "#000",
//     shadowOffset: { width: 0, height: 2 },
//     shadowOpacity: 0.05,
//     shadowRadius: 8,
//   },
//   showCoverImage: {
//     width: "100%",
//     height: dynamicSize(150),
//     backgroundColor: "#F0F0F5",
//   },
//   showCardBody: {
//     padding: dynamicSpacing(14),
//   },
//   showLiveTagRow: {
//     flexDirection: "row",
//     alignItems: "center",
//     marginBottom: dynamicSpacing(6),
//     gap: dynamicSpacing(6),
//   },
//   liveIndicatorDot: {
//     width: dynamicSize(6),
//     height: dynamicSize(6),
//     borderRadius: dynamicSize(3),
//     backgroundColor: "#FF9900",
//   },
//   showLiveTagText: {
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(10),
//     color: "#FF9900",
//     letterSpacing: 0.8,
//   },
//   showTitle: {
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(18),
//     color: "#1A1A1A",
//     lineHeight: dynamicFontSize(22),
//     marginBottom: dynamicSpacing(8),
//   },
//   showInfoMetaRow: {
//     flexDirection: "row",
//     alignItems: "center",
//     backgroundColor: "#FFFBF4",
//     paddingHorizontal: dynamicSpacing(10),
//     paddingVertical: dynamicSpacing(6),
//     borderRadius: dynamicSize(8),
//     alignSelf: "flex-start",
//     marginBottom: dynamicSpacing(8),
//     borderWidth: 1,
//     borderColor: "#FFE6C2",
//   },
//   showInfoItem: {
//     flexDirection: "row",
//     alignItems: "center",
//     gap: dynamicSpacing(4),
//   },
//   showInfoText: {
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(12),
//     color: "#D48300",
//   },
//   showMetaDivider: {
//     width: 1,
//     height: dynamicSize(12),
//     backgroundColor: "#FFD699",
//     marginHorizontal: dynamicSpacing(10),
//   },
//   showDescription: {
//     fontFamily: "Sen-Regular",
//     fontSize: dynamicFontSize(12),
//     color: "#666",
//     lineHeight: dynamicFontSize(17),
//   },

//   // HERO BRANDING SECTION (Promoter + Venue)
//   brandingWrapper: {
//     marginBottom: dynamicSpacing(25),
//     gap: dynamicSpacing(10),
//   },
//   brandHeroCard: {
//     flexDirection: "row",
//     alignItems: "center",
//     backgroundColor: "white",
//     padding: dynamicSpacing(14),
//     borderRadius: dynamicSize(16),
//     elevation: 2,
//     shadowColor: "#000",
//     shadowOffset: { width: 0, height: 1 },
//     shadowOpacity: 0.05,
//     shadowRadius: 4,
//   },
//   venueHeroCard: {
//     flexDirection: "row",
//     alignItems: "center",
//     backgroundColor: "#F4F4F8",
//     padding: dynamicSpacing(12),
//     borderRadius: dynamicSize(14),
//     borderWidth: 1,
//     borderColor: "#EAEAEF",
//   },
//   brandImage: {
//     width: dynamicSize(56),
//     height: dynamicSize(56),
//     borderRadius: dynamicSize(28),
//     backgroundColor: "#F5F5F7",
//   },
//   venueImage: {
//     width: dynamicSize(42),
//     height: dynamicSize(42),
//     borderRadius: dynamicSize(10),
//     backgroundColor: "#E5E5EA",
//   },
//   brandMetaInfo: {
//     flex: 1,
//     marginLeft: dynamicSpacing(12),
//     justifyContent: "center",
//   },
//   badgeRow: {
//     flexDirection: "row",
//     alignItems: "center",
//     marginBottom: dynamicSpacing(2),
//   },
//   promoterTag: {
//     backgroundColor: "#E8F0FE",
//     color: Colours.PRIMARY || "#2A65F0",
//     fontSize: dynamicFontSize(9),
//     fontFamily: "Sen-Bold",
//     paddingHorizontal: dynamicSpacing(6),
//     paddingVertical: dynamicSpacing(2),
//     borderRadius: dynamicSize(4),
//   },
//   venueLabel: {
//     color: "#888",
//     fontSize: dynamicFontSize(9),
//     fontFamily: "Sen-Bold",
//     letterSpacing: 0.5,
//     marginBottom: dynamicSpacing(2),
//   },
//   nameTxt: {
//     color: Colours.DARKGRAY || "#1A1A1A",
//     fontSize: dynamicFontSize(16),
//     fontFamily: "Sen-Bold",
//   },
//   venueNameTxt: {
//     color: "#333",
//     fontSize: dynamicFontSize(14),
//     fontFamily: "Sen-Bold",
//   },
//   emailTxt: {
//     color: "#666",
//     fontSize: dynamicFontSize(12),
//     fontFamily: "Sen-Regular",
//   },

//   // TOGGLES & CARDS
//   toggleRow: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "center",
//     backgroundColor: "#fff",
//     padding: dynamicSpacing(12),
//     borderRadius: dynamicSize(12),
//     marginTop: dynamicSpacing(5),
//     borderWidth: 1,
//     borderColor: "#F0F0F0",
//   },
//   toggleTextContent: { flex: 1 },
//   toggleTitle: {
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(14),
//     color: Colours.DARKGRAY,
//   },
//   toggleSubTitle: {
//     fontFamily: "Sen-Regular",
//     fontSize: dynamicFontSize(11),
//     color: "gray",
//   },

//   sectionLabel: {
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(11),
//     color: "#888",
//     letterSpacing: 1.2,
//     marginBottom: dynamicSpacing(12),
//   },

//   flexCard: {
//     backgroundColor: "white",
//     borderRadius: dynamicSize(16),
//     padding: dynamicSpacing(16),
//     marginBottom: dynamicSpacing(5),
//     borderWidth: 1.5,
//     borderColor: "#EAEAEF",
//     elevation: 2,
//     shadowColor: "#000",
//     shadowOffset: { width: 0, height: 2 },
//     shadowOpacity: 0.03,
//     shadowRadius: 6,
//   },

//   flexCard2: {
//     backgroundColor: "white",
//     borderRadius: dynamicSize(16),
//     padding: dynamicSpacing(16),
//     marginTop: dynamicSpacing(15),
//     marginBottom: dynamicSpacing(5),
//     borderWidth: 1.5,
//     borderColor: "#EAEAEF",
//     elevation: 2,
//     shadowColor: "#000",
//     shadowOffset: { width: 0, height: 2 },
//     shadowOpacity: 0.03,
//     shadowRadius: 6,
//   },

//   flexCardHeader: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "center",
//     marginBottom: dynamicSpacing(12),
//   },
//   iconBadge: {
//     width: dynamicSize(42),
//     height: dynamicSize(42),
//     borderRadius: dynamicSize(10),
//     justifyContent: "center",
//     alignItems: "center",
//   },
//   flexTitle: {
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(16),
//     color: "#333",
//   },
//   flexSubTitle: {
//     fontFamily: "Sen-Regular",
//     fontSize: dynamicFontSize(12),
//     color: "gray",
//     marginTop: 1,
//   },
//   regularBodyText: {
//     fontFamily: "Sen-Regular",
//     fontSize: dynamicFontSize(13),
//     color: "#777",
//     lineHeight: dynamicFontSize(18),
//     paddingLeft: dynamicSpacing(2),
//   },

//   activeRegularCard: {
//     borderColor: Colours.PRIMARY,
//     backgroundColor: "#FCFAFF",
//   },
//   doingsCardBase: {
//     borderColor: "#FFE6C2",
//     backgroundColor: "#FFFDFB",
//     position: "relative",
//     overflow: "hidden",
//   },
//   activeDoingsCard: {
//     borderColor: "#FF9900",
//     backgroundColor: "#FFFBF4",
//     elevation: 4,
//     shadowOpacity: 0.08,
//   },
//   premiumRibbon: {
//     position: "absolute",
//     top: 0,
//     right: 0,
//     backgroundColor: "#FF9900",
//     paddingHorizontal: dynamicSpacing(10),
//     paddingVertical: dynamicSpacing(3),
//     borderBottomLeftRadius: dynamicSize(10),
//     flexDirection: "row",
//     alignItems: "center",
//     gap: 3,
//   },
//   premiumRibbonText: {
//     color: "white",
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(9),
//     letterSpacing: 0.5,
//   },

//   perksContainer: {
//     marginTop: dynamicSpacing(4),
//     gap: dynamicSpacing(10),
//   },
//   perkRow: {
//     flexDirection: "row",
//     alignItems: "flex-start",
//   },
//   perkIcon: {
//     marginRight: dynamicSpacing(8),
//     marginTop: dynamicSpacing(2),
//   },
//   perkText: {
//     flex: 1,
//     fontFamily: "Sen-Regular",
//     fontSize: dynamicFontSize(13),
//     color: "#4A4A4A",
//     lineHeight: dynamicFontSize(19),
//   },

//   // BOTTOM STICKY BAR
//   footerStickyBar: {
//     position: "absolute",
//     bottom: 0,
//     left: 0,
//     right: 0,
//     backgroundColor: "white",
//     paddingHorizontal: dynamicSpacing(20),
//     paddingTop: dynamicSpacing(15),
//     paddingBottom: Platform.OS === "ios" ? dynamicSpacing(35) : dynamicSpacing(20),
//     borderTopWidth: 1,
//     borderColor: "#EAEAEF",
//   },
//   proceedBtn: {
//     backgroundColor: Colours.PRIMARY,
//     flexDirection: "row",
//     justifyContent: "center",
//     alignItems: "center",
//     height: dynamicSize(54),
//     borderRadius: dynamicSize(15),
//     gap: dynamicSpacing(10),
//   },
//   proceedBtnText: {
//     color: "white",
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(16),
//   },

//   // VENUE ORDERING NOTICE CARD
//   venueNoticeCard: {
//     backgroundColor: "#FFFFFF",
//     borderRadius: dynamicSize(18),
//     padding: dynamicSpacing(18),
//     marginTop: dynamicSpacing(10),
//     marginBottom: dynamicSpacing(20),
//     borderWidth: 1,
//     borderColor: "#EAEAEF",
//     elevation: 3,
//     shadowColor: "#000",
//     shadowOffset: { width: 0, height: 2 },
//     shadowOpacity: 0.04,
//     shadowRadius: 8,
//   },
//   noticeIconHeader: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "center",
//     marginBottom: dynamicSpacing(12),
//   },
//   noticeIconBadge: {
//     width: dynamicSize(46),
//     height: dynamicSize(46),
//     borderRadius: dynamicSize(12),
//     backgroundColor: "#EEF4FF",
//     justifyContent: "center",
//     alignItems: "center",
//   },
//   noticeTag: {
//     backgroundColor: "#E8F0FE",
//     paddingHorizontal: dynamicSpacing(10),
//     paddingVertical: dynamicSpacing(4),
//     borderRadius: dynamicSize(6),
//   },
//   noticeTagText: {
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(10),
//     color: Colours.PRIMARY || "#2A65F0",
//     letterSpacing: 0.8,
//   },
//   noticeTitle: {
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(18),
//     color: "#1A1A1A",
//     marginBottom: dynamicSpacing(6),
//   },
//   noticeBodyText: {
//     fontFamily: "Sen-Regular",
//     fontSize: dynamicFontSize(13),
//     color: "#555555",
//     lineHeight: dynamicFontSize(19),
//     marginBottom: dynamicSpacing(16),
//   },
//   boldHighlight: {
//     fontFamily: "Sen-Bold",
//     color: Colours.PRIMARY || "#2A65F0",
//   },
//   noticeStepList: {
//     gap: dynamicSpacing(12),
//     marginBottom: dynamicSpacing(16),
//   },
//   noticeStepRow: {
//     flexDirection: "row",
//     alignItems: "flex-start",
//     gap: dynamicSpacing(10),
//   },
//   stepDot: {
//     width: dynamicSize(20),
//     height: dynamicSize(20),
//     borderRadius: dynamicSize(10),
//     backgroundColor: "#F0F0F5",
//     justifyContent: "center",
//     alignItems: "center",
//     marginTop: dynamicSpacing(1),
//   },
//   stepDotNumber: {
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(11),
//     color: "#444444",
//   },
//   stepText: {
//     flex: 1,
//     fontFamily: "Sen-Regular",
//     fontSize: dynamicFontSize(12),
//     color: "#4A4A4A",
//     lineHeight: dynamicFontSize(17),
//   },
//   calloutBox: {
//     flexDirection: "row",
//     alignItems: "center",
//     backgroundColor: "#FFFBF4",
//     padding: dynamicSpacing(12),
//     borderRadius: dynamicSize(12),
//     borderWidth: 1,
//     borderColor: "#FFE6C2",
//   },
//   calloutIcon: {
//     marginRight: dynamicSpacing(8),
//   },
//   calloutText: {
//     flex: 1,
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(12),
//     color: "#D48300",
//     lineHeight: dynamicFontSize(17),
//   },
// });
